<template>
  <div class="map">
    <l-map ref="map"
      v-model:zoom="zoom"
      :options="{attributionControl: false}"
      :min-zoom="5"
      :center="position"
    >
      <l-tile-layer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        layer-type="base"
        name="OpenStreetMap"
      ></l-tile-layer>

      <l-control position="topright">
        <div class="btn-group btn-group-sm" role="group" v-if="cities.length > 0">
          <button class="btn bg-black text-white dropdown-toggle small btn-city me-2" data-bs-toggle="dropdown" aria-expanded="false">
            {{ filteredCityName }}
          </button>
          <div class="dropdown-menu p-2 model-selector">
            <div v-for="(city, i) in cities" :key="'city' + i" class="form-check">
              <input class="form-check-input" type="radio" :value="city.id" :id="'city-' + i" @change="changeCity($event.target.value)" :checked="city.id === currentCity">
              <label class="form-check-label small" :for="'city-' + i">{{ city.name }} <small class="text-success">({{ city.count }})</small></label>
            </div>
          </div>
          <button class="btn bg-black text-white dropdown-toggle small btn-model" data-bs-toggle="dropdown" aria-expanded="false">
            Modele
          </button>
          <div class="dropdown-menu p-2 model-selector">
            <div v-for="(group, i) in groupedModels" :key="'modelGroup-' + i" class="mb-2">
              <div class="mb-2"><strong>{{ group.name }}</strong></div>
              <div v-for="(model, j) in group.models" :key="'model-' + j" class="form-check">
                <input class="form-check-input" type="checkbox" :value="model.externalId" :id="'model-' + j" @change="changeModels($event.target.value)" :checked="currentModels.includes(model.externalId)">
                <label class="form-check-label small" :for="'model-' + j">{{ model.name }} <small class="text-success">({{ model.count }})</small></label>
              </div>
            </div>
          </div>
        </div>
      </l-control>

      <l-control position="topleft">
        <div class="leaflet-bar">
          <button class="btn btn-sm bg-white border-0" @click="locate" :disabled="isLocating">
            <fa-icon icon="fa-solid fa-crosshairs" />
          </button>
        </div>
      </l-control>

      <l-marker
        v-for="(marker, key) in filteredCarsMarker"
        :key="'marker-' + marker.vin"
        :ref="'marker-' + marker.vin"
        :lat-lng="[marker.coordinate.latitude, marker.coordinate.longitude]"
      >
        <l-icon
          shadow-url="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACkAAAApCAQAAAACach9AAACMUlEQVR4Ae3ShY7jQBAE0Aoz/f9/HTMzhg1zrdKUrJbdx+Kd2nD8VNudfsL/Th///dyQN2TH6f3y/BGpC379rV+S+qqetBOxImNQXL8JCAr2V4iMQXHGNJxeCfZXhSRBcQMfvkOWUdtfzlLgAENmZDcmo2TVmt8OSM2eXxBp3DjHSMFutqS7SbmemzBiR+xpKCNUIRkdkkYxhAkyGoBvyQFEJEefwSmmvBfJuJ6aKqKWnAkvGZOaZXTUgFqYULWNSHUckZuR1HIIimUExutRxwzOLROIG4vKmCKQt364mIlhSyzAf1m9lHZHJZrlAOMMztRRiKimp/rpdJDc9Awry5xTZCte7FHtuS8wJgeYGrex28xNTd086Dik7vUMscQOa8y4DoGtCCSkAKlNwpgNtphjrC6MIHUkR6YWxxs6Sc5xqn222mmCRFzIt8lEdKx+ikCtg91qS2WpwVfBelJCiQJwvzixfI9cxZQWgiSJelKnwBElKYtDOb2MFbhmUigbReQBV0Cg4+qMXSxXSyGUn4UbF8l+7qdSGnTC0XLCmahIgUHLhLOhpVCtw4CzYXvLQWQbJNmxoCsOKAxSgBJno75avolkRw8iIAFcsdc02e9iyCd8tHwmeSSoKTowIgvscSGZUOA7PuCN5b2BX9mQM7S0wYhMNU74zgsPBj3HU7wguAfnxxjFQGBE6pwN+GjME9zHY7zGp8wVxMShYX9NXvEWD3HbwJf4giO4CFIQxXScH1/TM+04kkBiAAAAAElFTkSuQmCC"
          :icon-size="[30, 42]"
          :popup-anchor="[0, -42]"
          :icon-anchor="[15, 42]"
          class-name=""
        >
          <div v-if="marker.discount" class="badge badge-promo rounded-1 px-1 text-bg-success text-start position-absolute" style="top:-15px;">{{ marker.discount }} zł</div>
          <img :src="'/img/icons/pin-' + (marker.modelName.startsWith('RENAULT') ? 'renault' : 'dacia') + '.png'" style="width:30px; height:42px;" alt="">
        </l-icon>
        <l-popup :ref="'popup' + key">
          <div class="text-center mb-3">
            <img :src="marker.thumbnailUrl" width="150">
          </div>
          <div>
            <h5><span class="badge rounded-1 px-1 text-bg-success">{{ marker.number }}</span> {{ marker.modelName }}</h5>
            <div class="mb-1">
              <span class="tag px-2 rounded-1">VIN: {{ marker.vin }}</span>
              <span class="tag px-2 rounded-1 ms-1" v-if="marker.regNumber">{{ marker.regNumber }}</span>
            </div>
            <div class="small">
              <span><fa-icon icon="fa-gas-pump" /> {{ marker.fuel }}% (~{{ marker.range }}km)</span>
              <span v-if="marker.location" class="small ms-2"><fa-icon icon="fa-location-dot" /> {{ marker.location }}</span>
              <span v-if="marker.discount" class="small ms-2 badge badge-promo rounded-1 px-1 text-bg-success text-start"><fa-icon icon="fa-gift" /> +{{ marker.discount }} zł</span>
            </div>
          </div>
        </l-popup>
      </l-marker>

      <l-marker
        v-if="currentPosition.latitude !== null && currentPosition.longitude !== null"
        :key="'zoom-' + zoom + '-pos'"
        :lat-lng="[currentPosition.latitude, currentPosition.longitude]"
      >
        <l-icon
          icon-url="/img/icons/pin-me.png"
          shadow-url="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACkAAAApCAQAAAACach9AAACMUlEQVR4Ae3ShY7jQBAE0Aoz/f9/HTMzhg1zrdKUrJbdx+Kd2nD8VNudfsL/Th///dyQN2TH6f3y/BGpC379rV+S+qqetBOxImNQXL8JCAr2V4iMQXHGNJxeCfZXhSRBcQMfvkOWUdtfzlLgAENmZDcmo2TVmt8OSM2eXxBp3DjHSMFutqS7SbmemzBiR+xpKCNUIRkdkkYxhAkyGoBvyQFEJEefwSmmvBfJuJ6aKqKWnAkvGZOaZXTUgFqYULWNSHUckZuR1HIIimUExutRxwzOLROIG4vKmCKQt364mIlhSyzAf1m9lHZHJZrlAOMMztRRiKimp/rpdJDc9Awry5xTZCte7FHtuS8wJgeYGrex28xNTd086Dik7vUMscQOa8y4DoGtCCSkAKlNwpgNtphjrC6MIHUkR6YWxxs6Sc5xqn222mmCRFzIt8lEdKx+ikCtg91qS2WpwVfBelJCiQJwvzixfI9cxZQWgiSJelKnwBElKYtDOb2MFbhmUigbReQBV0Cg4+qMXSxXSyGUn4UbF8l+7qdSGnTC0XLCmahIgUHLhLOhpVCtw4CzYXvLQWQbJNmxoCsOKAxSgBJno75avolkRw8iIAFcsdc02e9iyCd8tHwmeSSoKTowIgvscSGZUOA7PuCN5b2BX9mQM7S0wYhMNU74zgsPBj3HU7wguAfnxxjFQGBE6pwN+GjME9zHY7zGp8wVxMShYX9NXvEWD3HbwJf4giO4CFIQxXScH1/TM+04kkBiAAAAAElFTkSuQmCC"
          :icon-size="[30,42]"
          :tooltip-anchor="[0,-42]"
          :icon-anchor="[15, 42]"
        />
        <l-tooltip :options="{direction: 'top'}">Twoja pozycja</l-tooltip>

      </l-marker>

      <l-control-attribution prefix="<span style='font-size: 9px;'><a href='https://leafletjs.com' target='_blank'>Leaflet</a> | &copy; <a href='https://www.openstreetmap.org/copyright' target='_blank'>OpenStreetMap</a> contributors</span>" />
    </l-map>
  </div>
</template>

<script>
import "leaflet/dist/leaflet.css";

import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LControl,
  LControlAttribution,
  LIcon,
  LTooltip
} from '@vue-leaflet/vue-leaflet';

import { nextTick } from 'vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CarsMap',
  components: {
    LControl,
    LTooltip,
    LControlAttribution,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LIcon
  },
  props: {
    carMarkers: {
      type: Array,
      default: () => {},
    },
    cities: {
      type: Object,
      default: () => {}
    },
    models: {
      type: Array,
      default: () => []
    }
  },
  mounted() {
    this.locate();
  },
  computed: {
    ...mapGetters({
      isOrientationPortrait: 'device/isOrientationPortrait',
      currentPosition: 'geolocation/getCurrentPosition',
      currentCity: 'map/getCity',
      currentModels: 'map/getModels',
    }),
    filteredCarsMarker() {
      let cars = this.carMarkers[this.currentCity] || [];
      return cars.filter((car) => this.currentModels.includes(car.modelId));
    },
    filteredCityName() {
      return this.cities.find((c) => c.id === this.currentCity)?.name || '';
    },
    groupedModels() {
      const groups = ['Samochody osobowe', 'Samochody dostawcze'];
      const carModels = {};
      const cars = this.carMarkers[this.currentCity] || [];

      for (const model of this.models) {
        carModels[model.externalId] = [];
      }
      for (const car of cars) {
        carModels[car.modelId].push(car);
      }

      return [0, 1].map(index => {
        return {
          name: groups[index],
          models: this.models.filter(model => model.isTransporter === (index === 1)).map(m => {
            m.count = carModels[m.externalId]?.length || 0;
            return m;
          })
        }
      });
    },
  },
  methods: {
    ...mapActions({
      refreshCurrentPosition: 'geolocation/refreshCurrentPosition',
      setCity: 'map/setCity',
      setModels: 'map/setModels',
      addModel: 'map/addModel',
      removeModel: 'map/removeModel',
    }),
    changeModels(model) {
      const m = parseInt(model);
      if (this.currentModels.includes(m)) {
        this.removeModel(m);
      } else {
        this.addModel(m);
      }
    },
    async locate() {
      this.isLocating = true;
      this.position = [this.position[0] - 0.00001, this.position[1]];
      await this.refreshCurrentPosition((p) => this.position = [p.latitude, p.longitude]);
      this.isLocating = false;
    },
    changeCity(city) {
      this.setCity(city);
      this.$emit('change-city', city);
    },
    changePosition(lat, lon) {
      this.position = [lat, lon];
      this.zoom = 12;
    },
    locateCar(car) {
      const coordinate = car.coordinate;
      this.setCity(car.cityId);
      this.addModel(car.modelId);
      const map = this.$refs.map.leafletObject;
      nextTick(() => {
        map.setView([coordinate.latitude, coordinate.longitude], 17);
        nextTick(() => {
          this.$refs['marker-' + car.vin][0].leafletObject.openPopup(null, null, { autoPan: false });
        });
      })
    }
  },
  data() {
    return {
      isLocating: false,
      zoom: 12,
      position: [52.409538, 16.931992], // default Poznań
    };
  },
}
</script>

<style lang="scss" scoped>
.map {
  height: 100%;
  position: relative;
  .model-selector {
    font-size: 14px;
  }
  .btn-city, .btn-model {
    min-width: 100px;
  }
  .badge {
    width: 65px;
    color: #fff;
    &.badge-promo {
      width: auto;
    }
  }
  h5 {
    color: #333;
  }
  .tag {
    border: 1px solid #999;
    color: #999;
    font-size: 11px;
    padding: 2px;
    a {
      color: inherit;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>